/**
 * Gallery topicXData helpers — FE-owned JSON on the GALLERY topic.
 * `sourceAlbumId` remains the primary/back-compat field; `sourceAlbumIds`
 * holds the full linked set for multi-album Studio Assets.
 */

export interface GalleryXData {
  sourceAlbumId?: string
  sourceAlbumIds?: string[]
  /** Wizard / other fields — preserved when we rewrite album links. */
  [key: string]: unknown
}

function parseRaw(raw?: string | null): GalleryXData {
  if (!raw || raw === 'X Data Here') return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as GalleryXData
  } catch {
    return {}
  }
}

/** Ordered unique album ids — primary first when present. */
export function parseSourceAlbumIds(topicXData?: string | null): string[] {
  const data = parseRaw(topicXData)
  const ids: string[] = []
  const push = (id: unknown) => {
    if (id == null || id === '') return
    const s = String(id)
    if (!ids.includes(s)) ids.push(s)
  }
  push(data.sourceAlbumId)
  if (Array.isArray(data.sourceAlbumIds)) {
    for (const id of data.sourceAlbumIds) push(id)
  }
  return ids
}

export function parseSourceAlbumId(topicXData?: string | null): string | undefined {
  return parseSourceAlbumIds(topicXData)[0]
}

/** Merge album-link fields into existing xdata without wiping wizard keys. */
export function withSourceAlbumIds(topicXData: string | undefined | null, albumIds: string[]): string {
  const data = parseRaw(topicXData)
  const unique = Array.from(new Set(albumIds.map(String).filter(Boolean)))
  const next: GalleryXData = { ...data }
  if (unique.length === 0) {
    delete next.sourceAlbumId
    delete next.sourceAlbumIds
  } else {
    next.sourceAlbumId = unique[0]
    next.sourceAlbumIds = unique
  }
  return JSON.stringify(next)
}
